import type { Session, User } from "@/api/types";

/**
 * The signed-in session, kept outside React so the API client can read it.
 *
 * The access token lives in memory only. The refresh token is kept in localStorage
 * so a reload or a new tab stays signed in. That is a known trade-off (a script
 * injected into the page could read it); the planned hardening is an httpOnly
 * cookie for the web console. See docs/PROJECT.md, "Open decisions".
 */
export type SessionState = {
  status: "loading" | "signed-out" | "signed-in";
  user: User | null;
};

const REFRESH_KEY = "zotula.refresh";

let state: SessionState = { status: "loading", user: null };
let accessToken: string | null = null;
const listeners = new Set<() => void>();

function emit(next: SessionState) {
  state = next;
  for (const listener of listeners) listener();
}

function readStored(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function writeStored(token: string | null) {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {
    // Storage can be blocked (private mode). The session then lasts until reload.
  }
}

let memoryRefresh: string | null = null;

export const session = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getState: (): SessionState => state,
  getAccessToken: (): string | null => accessToken,
  getRefreshToken: (): string | null => readStored() ?? memoryRefresh,

  /** Store a fresh token pair after sign-in or refresh. */
  set(next: Session) {
    accessToken = next.access_token;
    memoryRefresh = next.refresh_token;
    writeStored(next.refresh_token);
    emit({ status: "signed-in", user: next.user });
  },

  /** Show changed details of the signed-in person (after they edit themselves) without a new token. */
  updateUser(user: User) {
    if (state.status === "signed-in" && state.user?.id === user.id) emit({ status: "signed-in", user });
  },

  clear() {
    accessToken = null;
    memoryRefresh = null;
    writeStored(null);
    emit({ status: "signed-out", user: null });
  },
};

// Signing out in one tab signs out the others.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === REFRESH_KEY && event.newValue === null && state.status === "signed-in") {
      accessToken = null;
      memoryRefresh = null;
      emit({ status: "signed-out", user: null });
    }
  });
}
